import * as THREE from "three";
import vertex from "./shader/vertex.glsl";
import fragment from "./shader/fragment.glsl";
import { GUI } from "lil-gui";

class Gradient {
	constructor(colors, segmentsSize, materialArgs = {}, config = {}) {
		this.colors = colors;
		this.segmentsSize = segmentsSize;
		this.materialArgs = materialArgs;
		this.animate = this.animate.bind(this);
		this.debug = false;
		this.config = {
			seed: 1,
			noiseX: 2.75,
			noiseY: 4.47,
			autoplay: true,
			...config,
			cameraPosition: { x: -0.18, y: 0.41, z: 0.59, ...config.cameraPosition },
			cameraRotation: { x: 0.34, y: -0.18, z: 0.23, ...config.cameraRotation },
		};
		this.init();
	}

	convertColorsToPalette(colors = []) {
		return colors.map((color) => new THREE.Color(color));
	}

	init() {
		this.createScene();

		this.material = new THREE.ShaderMaterial({
			...this.materialArgs,
			extensions: {
				derivatives: "#extension GL_OES_standard_derivatives : enable",
			},
			uniforms: {
				time: { value: 0 },
				uColor: { value: this.convertColorsToPalette(this.colors) },
				resolution: { value: new THREE.Vector4() },
				noiseX: { value: this.config.noiseX },
				noiseY: { value: this.config.noiseY },
				seed: { value: this.config.seed },
			},
			defines: {
				N_COLORS: this.colors.length,
			},
			vertexShader: vertex,
			fragmentShader: fragment,
		});

		this.geometry = new THREE.PlaneGeometry(2.5, 2.5, this.segmentsSize, this.segmentsSize);

		this.plane = new THREE.Mesh(this.geometry, this.material);
		this.scene.add(this.plane);

		this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.renderer.physicallyCorrectLights = true;

		if (this.config.autoplay) {
			this.renderer.setAnimationLoop(this.animate);
		}

		window.addEventListener("resize", () => {
			this.resize();
		});
	}

	pause() {
		this.renderer.setAnimationLoop(null);
	}

	play() {
		this.renderer.setAnimationLoop(this.animate);
	}

	createScene() {
		this.scene = new THREE.Scene();
		this.camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 10);
		this.camera.position.x = this.config.cameraPosition.x;
		this.camera.position.y = this.config.cameraPosition.y;
		this.camera.position.z = this.config.cameraPosition.z;

		this.camera.rotation.x = this.config.cameraRotation.x;
		this.camera.rotation.y = this.config.cameraRotation.y;
		this.camera.rotation.z = this.config.cameraRotation.z;

		// this.camera.lookAt(0, 0, 0);
	}

	canvas() {
		return this.renderer.domElement;
	}

	animate(time) {
		this.material.uniforms.time.value = time * 0.0001;
		this.renderer.render(this.scene, this.camera);
	}

	resize() {
		const size = this.canvas();
		const aspect = window.innerWidth / window.innerHeight;

		this.camera.aspect = aspect;
		this.camera.updateProjectionMatrix();
		// this.renderer.setSize(window.innerWidth, window.innerHeight);

		this.calibratePosition();
	}

	//#region Debug - remove when not in use / going live to save space.

	addColor() {
		this.colors.push("#ffffff");
		const palette = this.convertColorsToPalette(this.colors);
		this.material.uniforms.uColor.value = palette;
		this.material.defines.N_COLORS = this.colors.length;
		this.material.needsUpdate = true;
		this.initDebug();
	}

	removeColor(index = this.colors.length - 1) {
		if (this.colors.length <= 1) return;
		this.colors.splice(index, 1);
		this.material.uniforms.uColor.value = this.convertColorsToPalette(this.colors);
		this.material.defines.N_COLORS = this.colors.length;
		this.material.needsUpdate = true;
		this.initDebug();
	}

	initDebug() {
		if (this.gui) this.gui.destroy();
		this.gui = new GUI({
			title: "Gradient GUI",
		});

		const colors = this.gui.addFolder("Colors");

		let colorObj = {};
		this.colors.forEach((_, index) => {
			colorObj[`color${index}`] = this.colors[index];
		});

		Object.keys(colorObj).forEach((key, i) => {
			colors.addColor(colorObj, key).onChange((color) => {
				this.colors[i] = color;
				this.material.uniforms.uColor.value[i] = new THREE.Color(color);
			});
		});

		colors.add(this, "addColor").name("Add Color");
		colors.add(this, "removeColor").name("Remove Color");

		const mesh = this.gui.addFolder("Mesh");
		mesh.add(this.material, "wireframe");

		mesh.add(this.material.uniforms.noiseX, "value").min(0).max(10).step(0.01).name("Noise X");
		mesh.add(this.material.uniforms.noiseY, "value").min(0).max(10).step(0.01).name("Noise Y");

		const cameraPosition = this.gui.addFolder("Camera Position");
		cameraPosition.add(this.camera.position, "x").min(-1).max(1).step(0.01).name("Position X");
		cameraPosition.add(this.camera.position, "y").min(-1).max(1).step(0.01).name("Position Y");
		cameraPosition.add(this.camera.position, "z").min(0).max(1).step(0.01).name("Position Z");

		const cameraRotation = this.gui.addFolder("Camera Rotation");
		cameraRotation.add(this.camera.rotation, "x").min(-1).max(1).step(0.01).name("Rotation X");
		cameraRotation.add(this.camera.rotation, "y").min(-1).max(1).step(0.01).name("Rotation Y");
		cameraRotation.add(this.camera.rotation, "z").min(-2).max(2).step(0.01).name("Rotation Z");

		this.calibratePosition();
	}

	calibratePosition() {
		if (!this.debug) return;

		this.gui.domElement.style.position = "absolute";
		this.gui.domElement.style.top = "20px";
		this.gui.domElement.style.left = "0";

		const canvas = this.canvas().getBoundingClientRect();
		if (canvas.top > 0) {
			let offset = 0;
			if (window.scrollY > canvas.top) {
				offset = window.scrollY;
			}
			this.gui.domElement.style.top = `${canvas.top + offset + 20}px`;
		}
		this.gui.domElement.style.left = `${canvas.width - this.gui.domElement.offsetWidth - 20}px`;
	}

	enableDebug() {
		this.debug = true;
		this.initDebug();
	}

	//#endregion
}

export default Gradient;
